import {
	createContext,
	FunctionComponent,
	PropsWithChildren,
	useCallback,
	useRef,
	useState,
} from "react";
import { EVENTS, Joyride, STATUS, type EventData, type Step } from "react-joyride";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { tourRegistry } from "./tours";

const TOUR_SEEN_PREFIX = "tour-seen:";
const TOUR_PENDING_PREFIX = "tour-pending:";
const TARGET_CHECK_DELAY = 500;
const TARGET_CHECK_RETRIES = 5;

export type TourContextValue = {
	startTour: (tourId: string, context?: Record<string, unknown>) => void;
	startPendingTour: (tourId: string, context?: Record<string, unknown>) => void;
	stopTour: () => void;
	isTourActive: (tourId: string) => boolean;
};

export const TourContext = createContext<TourContextValue | null>(null);

export const TourProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [activeTour, setActiveTour] = useState<string | null>(null);
	const [steps, setSteps] = useState<Step[]>([]);
	const [run, setRun] = useState(false);
	const queueRef = useRef<{ tourId: string; context?: Record<string, unknown> }[]>([]);
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const activeTourRef = useRef<string | null>(null);
	const tourEndedRef = useRef(false);

	const markSeen = useCallback((tourId: string) => {
		localStorage.setItem(`${TOUR_SEEN_PREFIX}${tourId}`, "true");
	}, []);

	const isSeen = useCallback((tourId: string) => {
		return localStorage.getItem(`${TOUR_SEEN_PREFIX}${tourId}`) === "true";
	}, []);

	const processQueue = useCallback(() => {
		if (queueRef.current.length === 0) {
			return;
		}

		const next = queueRef.current.shift();

		if (!next) {
			return;
		}

		const definition = tourRegistry[next.tourId];

		if (!definition) {
			processQueue();

			return;
		}

		const resolvedSteps = definition.steps(t, next.context);

		setActiveTour(next.tourId);
		activeTourRef.current = next.tourId;
		tourEndedRef.current = false;
		setSteps(resolvedSteps);
		setRun(true);
	}, [t]);

	const waitForTarget = useCallback(
		(tourId: string, resolvedSteps: Step[], retriesLeft: number) => {
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current);
			}

			const firstTarget = resolvedSteps[0]?.target;

			if (!firstTarget || typeof firstTarget !== "string") {
				setActiveTour(tourId);
				activeTourRef.current = tourId;
				tourEndedRef.current = false;
				setSteps(resolvedSteps);
				setRun(true);

				return;
			}

			const el = document.querySelector(firstTarget);

			if (el) {
				setActiveTour(tourId);
				activeTourRef.current = tourId;
				tourEndedRef.current = false;
				setSteps(resolvedSteps);
				setRun(true);

				return;
			}

			if (retriesLeft <= 0) {
				return;
			}

			retryTimerRef.current = setTimeout(() => {
				waitForTarget(tourId, resolvedSteps, retriesLeft - 1);
			}, TARGET_CHECK_DELAY);
		},
		[]
	);

	const startTour = useCallback(
		(tourId: string, context?: Record<string, unknown>) => {
			if (isSeen(tourId)) {
				return;
			}

			const definition = tourRegistry[tourId];

			if (!definition) {
				return;
			}

			if (activeTour) {
				queueRef.current.push({ tourId, context });

				return;
			}

			const resolvedSteps = definition.steps(t, context);

			waitForTarget(tourId, resolvedSteps, TARGET_CHECK_RETRIES);
		},
		[activeTour, isSeen, t, waitForTarget]
	);

	const startPendingTour = useCallback(
		(tourId: string, context?: Record<string, unknown>) => {
			const pendingKey = `${TOUR_PENDING_PREFIX}${tourId}`;
			const isPending = localStorage.getItem(pendingKey) === "true";

			if (!isPending || isSeen(tourId)) {
				return;
			}

			localStorage.removeItem(pendingKey);
			startTour(tourId, context);
		},
		[isSeen, startTour]
	);

	const stopTour = useCallback(() => {
		setRun(false);
		setActiveTour(null);
		activeTourRef.current = null;
		setSteps([]);
	}, []);

	const isTourActive = useCallback((tourId: string) => activeTour === tourId, [activeTour]);

	const handleEvent = useCallback(
		(data: EventData) => {
			const { status, type } = data;

			// Guard: after we handle tour:end, Joyride may fire more events
			// (tour:status, tour:start) before React re-renders and unmounts the component.
			if (tourEndedRef.current) {
				return;
			}

			if (type === EVENTS.TOUR_END && (status === STATUS.FINISHED || status === STATUS.SKIPPED)) {
				tourEndedRef.current = true;
				const currentTour = activeTourRef.current;

				if (currentTour) {
					markSeen(currentTour);

					// If tour completed (not skipped) and has a nextTour, persist it as pending
					if (status === STATUS.FINISHED) {
						const definition = tourRegistry[currentTour];

						if (definition?.nextTour) {
							localStorage.setItem(`${TOUR_PENDING_PREFIX}${definition.nextTour}`, "true");
						}

						if (definition?.navigateOnFinish) {
							const route = definition.navigateOnFinish;

							setRun(false);
							setActiveTour(null);
							activeTourRef.current = null;
							setSteps([]);

							setTimeout(() => {
								navigate(route);
								processQueue();
							}, 300);

							return;
						}
					}
				}

				setRun(false);
				setActiveTour(null);
				activeTourRef.current = null;
				setSteps([]);

				// Process next tour in queue
				setTimeout(() => processQueue(), 300);
			}
		},
		[markSeen, navigate, processQueue]
	);

	return (
		<TourContext.Provider value={{ startTour, startPendingTour, stopTour, isTourActive }}>
			{children}
			{activeTour && (
				<Joyride
					key={activeTour}
					onEvent={handleEvent}
					continuous={true}
					run={run}
					scrollToFirstStep={true}
					steps={steps}
					locale={{
						skip: t("featureOnboarding.skipTour"),
						last: t("featureOnboarding.gotIt"),
					}}
					options={{
						buttons: ["back", "close", "primary", "skip"],
						primaryColor: "#4D66F5",
						zIndex: 10000,
					}}
				/>
			)}
		</TourContext.Provider>
	);
};
